import { useRef } from 'react';

import { useTheme } from '@mui/material';

import useBaseChartOptions from '@/components/Chart/hooks/options/useBaseChartOptions';
import getFullscreenTooltip from '@/components/Chart/hooks/tooltip/getFullscreenTooltip';
import useSpotTooltipFormatter from '@/components/Chart/hooks/tooltip/useSpotTooltipFormatter';
import type {
  ExtendedOptions,
  ExtendedSeriesOptionsType
} from '@/components/Chart/types/highcharts';
import {
  DEFAULT_CHART_HEIGHT,
  fullScreenChartPalette,
  regularChartPalette
} from '@/constants/constants';
import useSelectionFunctions from '@/hooks/useSelectionFunctions';
import useUserDetails from '@/hooks/useUserDetails';
import type { SelectedSymbol } from '@/types/symbols';

import useChartFunctions from './useChartFunctions';

import type { ChartEventsOptions, SeriesClickEventObject } from 'highcharts';
import type Highcharts from 'highcharts/highstock';

const useSeriesClick = () => {
  const { addSymbolToSelection, isSymbolSelected, removeSymbolFromSelection } =
    useSelectionFunctions();

  const onSeriesClick = (event: SeriesClickEventObject) => {
    // @ts-expect-error: highcharts rules.
    const metadata: SymbolKey = event.point.series.userOptions.custom;

    const symbol: SelectedSymbol = {
      Code: metadata.Code,
      Period: metadata.Period,
      PeriodType: metadata.PeriodType,
      TimeRef: metadata.TimeRef
    };
    if (isSymbolSelected(symbol)) {
      removeSymbolFromSelection(symbol);
    } else {
      addSymbolToSelection(symbol);
    }
  };
  return { onSeriesClick };
};

const useFullScreenOptions = ({
  height,
  isDataHub,
  isLightMode
}: {
  height?: number;
  isDataHub?: boolean;
  isLightMode: boolean;
}) => {
  const { isUserBasic, isUserExpired } = useUserDetails();
  const theme = useTheme();
  const createTooltipFormatter = useSpotTooltipFormatter(isDataHub ? 'Full Title' : undefined);
  const { tooltip } = getFullscreenTooltip(createTooltipFormatter, theme);
  const { createPlotOptions, createEventOptions } = useChartFunctions();
  const { createBaseChartOptions } = useBaseChartOptions(height ?? DEFAULT_CHART_HEIGHT);

  const { onSeriesClick } = useSeriesClick();

  const plotOptions = createPlotOptions();

  if (!isDataHub && plotOptions.series) {
    plotOptions.series.events = {
      click: onSeriesClick
    };
  }
  const watermarkImage = useRef<Highcharts.SVGElement | null>(null);
  const textColor = isLightMode ? theme.palette.background.paper : theme.palette.text.primary;
  const backgroundColor = isLightMode ? theme.palette.text.primary : theme.palette.background.paper;
  const getWatermarkAttributes = (chart: Highcharts.Chart) =>
    isUserBasic || isUserExpired
      ? {
          // Center watermark, responsive size based on chart dimensions
          width: chart.chartWidth < 1025 ? 190 * 3 : 950,
          height: chart.chartHeight < 750 ? 50 * 3 : 250,
          x: Math.round((chart.chartWidth - (chart.chartWidth < 1025 ? 190 * 3 : 950)) / 2),
          y: Math.round((chart.chartHeight - (chart.chartHeight < 750 ? 50 * 3 : 250)) / 2),
          zIndex: 1
        }
      : {
          // Bottom-right watermark with fixed margins for non-basic users
          width: 190,
          height: 50,
          x: Math.max(0, chart.chartWidth - 250),
          y: Math.max(0, chart.chartHeight - 220),
          zIndex: 1
        };

  const events: ChartEventsOptions = createEventOptions(
    watermarkImage,
    getWatermarkAttributes,
    isLightMode ? '/watermark-color.svg' : '/watermark-black.svg'
  );

  const createOptions = (
    chartSeries: ExtendedSeriesOptionsType[],
    seriesHidden: string[]
  ): ExtendedOptions => {
    chartSeries = chartSeries.map(series => {
      const { combinedIndex } = series.custom;
      const palette = isLightMode ? fullScreenChartPalette : regularChartPalette;
      series.color ??=
        combinedIndex > 5
          ? '#F8F8F8'
          : (series.custom.color ?? palette[combinedIndex % palette.length]);
      return series;
    });

    let options = createBaseChartOptions(
      chartSeries.map(series => {
        if (series.id && seriesHidden.includes(series.id)) {
          return {
            ...series,
            visible: false
          };
        }
        return series;
      }),
      textColor,
      backgroundColor
    );

    options = { ...options, chart: { ...options.chart, events } };
    options = { ...options, plotOptions };
    options = {
      ...options,
      exporting: {
        ...options.exporting,
        sourceWidth: 1000,
        sourceHeight: 420,
        buttons: undefined
      }
    };
    options = {
      ...options,
      stockTools: {
        gui: {
          buttons: [
            'indicators',
            'simpleShapes',
            'lines',
            'crookedLines',
            'measure',
            'advanced',
            'toggleAnnotations',
            'verticalLabels',
            'flags',
            'zoomChange',
            'currentPriceIndicator'
          ]
        }
      }
    };

    options = { ...options, tooltip };
    return options;
  };

  return { createOptions };
};

export default useFullScreenOptions;
